import { useMemo, useState } from 'react';
import { Alert, Button, Card, Dropdown, Typography } from 'antd';
import { DataTable } from '../../components/DataTable';
import { StatusTag } from '../../components/StatusTag';
import type { ColumnsType } from 'antd/es/table';
import {
  Bank,
  CaretDown,
  Clock,
  Info,
  PencilSimple,
  Plus,
  Storefront,
} from '@phosphor-icons/react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { PageBody, PageHeader } from '../../app/AppLayout';
import { ErrorState, LoadingState } from '../../components/PageStates';
import { isSuperAdmin, useCurrentStaff } from '../../auth/session';
import { tokens } from '../../theme';
import { branchBlockers, catalogApi, catalogKeys, type Branch, type Chain } from './api';
import { BranchFormModal } from './BranchFormModal';
import { BankAccountsDrawer } from './BankAccountsDrawer';
import { PosStoreCodesDrawer } from './PosStoreCodesDrawer';
import { BranchShiftsDrawer } from './BranchShiftsDrawer';
import { ChainFormModal } from './ChainFormModal';

/** `HH:mm:ss` từ backend về `HH:mm` để hiển thị. */
const toDisplayTime = (value: string): string => value.slice(0, 5);

/** Tọa độ hiển thị theo kiểu Việt Nam, dấu phẩy thập phân — giống prototype. */
const toDisplayCoordinate = (value: number): string =>
  value.toFixed(4).replace('.', ',');

/**
 * Thẻ 1 chuỗi + bảng chi nhánh của nó. Tách riêng khỏi `CatalogPage` vì mỗi thẻ là một
 * component riêng (tránh gọi hook bên trong callback của `chains.map()`).
 *
 * Dùng thẳng thanh công cụ chuẩn của `DataTable` (tìm kiếm · tải lại · mật độ · ẩn/hiện cột ·
 * toàn màn hình) — đồng bộ với mọi trang khác (đổi 2026-09-02). Tiêu đề `Card` chỉ còn tên
 * chuỗi và chip trạng thái; tìm kiếm client-side do `DataTable` tự lo trên `dataSource`.
 */
function ChainBranchesCard({
  chain,
  branches,
  columns,
  activeShiftCountByBranch,
  activeStoreCodeCountByBranch,
  storeCodesLoaded,
  canManageChains,
  onEditChain,
  onReload,
}: {
  chain: Chain;
  branches: Branch[];
  columns: ColumnsType<Branch>;
  activeShiftCountByBranch: Map<string, number>;
  activeStoreCodeCountByBranch: Map<string, number>;
  storeCodesLoaded: boolean;
  canManageChains: boolean;
  onEditChain: (chain: Chain) => void;
  onReload: () => void;
}) {
  // Chip trạng thái chuỗi — dùng chung cho cả chuỗi rỗng (header nhỏ) lẫn chuỗi có bảng (toolbarExtra).
  // Kèm nút sửa chuỗi cho super_admin: giới thiệu, ảnh bìa và logo chỉ sửa được ở đây (M14).
  const statusTag = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <StatusTag color={chain.status === 'Active' ? 'success' : 'default'}>
        {chain.status === 'Active' ? 'Đang hoạt động' : 'Ngừng hoạt động'}
      </StatusTag>
      {canManageChains && (
        <Button size="small" icon={<PencilSimple size={12} aria-hidden />} onClick={() => onEditChain(chain)}>
          Sửa chuỗi
        </Button>
      )}
    </div>
  );

  return (
    <Card style={{ marginBottom: 18 }} styles={{ body: { paddingTop: 8 } }}>
      {branches.length === 0 ? (
        // Chuỗi chưa có chi nhánh: không có bảng để mang tiêu đề, nên dựng một header gọn cho
        // chuỗi vẫn nhận ra được, rồi tới Alert.
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: 12,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}>{chain.name}</div>
              <div style={{ fontSize: 12, color: tokens.color.ink3 }}>
                Chưa có chi nhánh · mã <code>{chain.slug}</code>
              </div>
            </div>
            {statusTag}
          </div>
          <Alert
            type="info"
            showIcon
            icon={<Info size={18} aria-hidden />}
            message="Chuỗi này chưa có chi nhánh nào"
            description="Thành viên dùng chung một ví điểm cho mọi chuỗi, nhưng tỷ lệ tích điểm, danh mục quà và khuyến mãi vẫn cấu hình riêng theo từng chuỗi."
          />
        </>
      ) : (
        <DataTable
          // Tên chuỗi là tiêu đề bảng; chip trạng thái dời sang phải toolbar. Gộp header chuỗi và
          // thanh công cụ vào MỘT hàng thay vì tiêu đề Card riêng + toolbar riêng.
          title={chain.name}
          subTitle={
            <>
              {branches.length} chi nhánh · mã <code>{chain.slug}</code>
            </>
          }
          toolbarExtra={statusTag}
          onReload={onReload}
          searchPlaceholder="Tìm chi nhánh"
          rowKey="id"
          columns={columns}
          dataSource={branches}
          pagination={false}
          // Cửa sổ hẹp thì cuộn ngang, không bóp cột — 7 cột cộng thao tác đã vượt 1100px,
          // thiếu prop này thì bảng tràn ra ngoài card trên laptop nhỏ/nửa màn hình.
          scroll={{ x: 1190 }}
          // Tô nền cảnh báo hổ phách khi thiếu 1 trong 2 tiêu chí vận hành BẮT BUỘC: ca làm hoặc
          // mã cửa hàng POS (RL-214 — STK ngân hàng đã thành tùy chọn, không còn kích cảnh báo).
          // Chỉ tô khi ĐÃ tải xong cả số ca lẫn danh sách mã POS — tô sớm là nháy cảnh báo rồi tự tắt.
          rowClassName={(branch) => {
            if (!activeShiftCountByBranch.has(branch.id) || !storeCodesLoaded) return '';
            const blockers = branchBlockers(
              activeShiftCountByBranch.get(branch.id) ?? 0,
              activeStoreCodeCountByBranch.get(branch.id) ?? 0,
            );
            return blockers.length > 0 ? 'row-needs-attention' : '';
          }}
        />
      )}
    </Card>
  );
}

export function CatalogPage() {
  const staff = useCurrentStaff();
  const canManageChains = isSuperAdmin(staff);

  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [creatingBranchForChain, setCreatingBranchForChain] = useState<Chain | null>(null);
  const [bankAccountsBranch, setBankAccountsBranch] = useState<Branch | null>(null);
  const [storeCodesBranch, setStoreCodesBranch] = useState<Branch | null>(null);
  const [shiftsBranch, setShiftsBranch] = useState<Branch | null>(null);
  const [isChainModalOpen, setIsChainModalOpen] = useState(false);
  const [editingChain, setEditingChain] = useState<Chain | null>(null);

  const chainsQuery = useQuery({ queryKey: catalogKeys.chains, queryFn: catalogApi.listChains });
  const branchesQuery = useQuery({ queryKey: catalogKeys.branches, queryFn: catalogApi.listBranches });

  // Ca làm không nằm trong `GET /branches`, phải hỏi riêng từng chi nhánh.
  // Phase 1 có 6–10 chi nhánh nên vài lời gọi song song là chấp nhận được; nếu
  // số chi nhánh tăng thì thêm `activeShiftCount` vào BranchResponse thay vì
  // nhân số lời gọi lên.
  const shiftQueries = useQueries({
    queries: (branchesQuery.data ?? []).map((branch) => ({
      queryKey: catalogKeys.branchShifts(branch.id),
      queryFn: () => catalogApi.listBranchShifts(branch.id),
    })),
  });

  const activeShiftCountByBranch = useMemo(() => {
    const counts = new Map<string, number>();
    (branchesQuery.data ?? []).forEach((branch, index) => {
      const shifts = shiftQueries[index]?.data;
      // `undefined` khi còn đang tải — KHÔNG coi là 0, không thì bảng nháy
      // cảnh báo "chưa khai ca" một nhịp rồi tự tắt
      if (shifts !== undefined) counts.set(branch.id, shifts.filter((x) => x.isActive).length);
    });
    return counts;
  }, [branchesQuery.data, shiftQueries]);

  // Mã cửa hàng POS không nằm trong `GET /branches`. Một lời gọi trả TẤT CẢ mã của mọi chi nhánh
  // (Phase 1 chỉ vài chục mã), đếm theo chi nhánh ở client — rẻ hơn nhân lời gọi theo từng dòng.
  const posStoreCodesQuery = useQuery({
    queryKey: catalogKeys.posStoreCodes,
    queryFn: catalogApi.listPosStoreCodes,
  });

  const storeCodesLoaded = posStoreCodesQuery.data !== undefined;

  const activeStoreCodeCountByBranch = useMemo(() => {
    const counts = new Map<string, number>();
    for (const code of posStoreCodesQuery.data ?? []) {
      if (code.isActive) counts.set(code.branchId, (counts.get(code.branchId) ?? 0) + 1);
    }
    return counts;
  }, [posStoreCodesQuery.data]);

  const branchesByChain = useMemo(() => {
    const grouped = new Map<string, Branch[]>();
    for (const branch of branchesQuery.data ?? []) {
      const list = grouped.get(branch.chainId) ?? [];
      list.push(branch);
      grouped.set(branch.chainId, list);
    }
    return grouped;
  }, [branchesQuery.data]);

  const activeBranchCount = (branchesQuery.data ?? []).filter((x) => x.status === 'Active').length;

  const columns: ColumnsType<Branch> = [
    {
      title: 'Chi nhánh',
      key: 'name',
      render: (_, branch) => (
        <>
          <div style={{ fontWeight: 600 }}>{branch.name}</div>
          <div style={{ fontSize: 11.5, color: tokens.color.ink3, fontFamily: 'monospace' }}>
            {toDisplayCoordinate(branch.latitude)} · {toDisplayCoordinate(branch.longitude)}
          </div>
        </>
      ),
    },
    { title: 'Địa chỉ', dataIndex: 'address', key: 'address' },
    {
      title: 'Giờ mở cửa',
      key: 'hours',
      width: 150,
      render: (_, branch) => (
        <span style={{ fontFamily: 'monospace' }}>
          {toDisplayTime(branch.openTime)} tới {toDisplayTime(branch.closeTime)}
        </span>
      ),
    },
    {
      title: 'Bàn theo loại',
      key: 'tables',
      width: 190,
      render: (_, branch) => {
        const total = branch.tableClasses.reduce((sum, x) => sum + x.tableCount, 0);

        // Chi nhánh chưa khai bàn thì không tính được thời gian chờ (BR-505),
        // và lỗi đó chỉ lộ ra khi khách đầu tiên bốc số
        if (total === 0) {
          return <span style={{ color: tokens.color.warn }}>Chưa khai số bàn</span>;
        }

        return (
          <span className="num">
            {branch.tableClasses.map((x) => `${x.class} ${x.tableCount}`).join(' · ')}
          </span>
        );
      },
    },
    {
      title: 'Ca làm',
      key: 'shifts',
      width: 150,
      render: (_, branch) => {
        const count = activeShiftCountByBranch.get(branch.id);
        if (count === undefined) return <span style={{ color: tokens.color.ink3 }}>Đang tải</span>;

        return count > 0 ? (
          <StatusTag color="success" icon={<Clock size={12} aria-hidden />}>
            {count} ca
          </StatusTag>
        ) : (
          <StatusTag color="error">Chưa khai</StatusTag>
        );
      },
    },
    {
      title: 'Tài khoản nhận',
      key: 'bank',
      width: 160,
      render: (_, branch) =>
        branch.activeBankAccountCount > 0 ? (
          <StatusTag color="success" icon={<Bank size={12} aria-hidden />}>
            {branch.activeBankAccountCount} tài khoản
          </StatusTag>
        ) : (
          // RL-214: STK ngân hàng nay tùy chọn — dùng màu trung tính + chữ "(tùy chọn)" thay vì
          // đỏ, để không đọc thành lỗi. Vẫn cần khai nếu muốn quét VietQR (BR-202).
          <StatusTag color="default">Chưa khai (tùy chọn)</StatusTag>
        ),
    },
    {
      // Chi nhánh chưa khai mã POS thì hóa đơn định dạng POS trả `INVALID_STORE_CODE` (BR-215
      // lớp (a)). Cột hiện trạng thái để nền cảnh báo của dòng không phải tín hiệu chỉ-bằng-màu.
      title: 'Mã POS',
      key: 'posStoreCodes',
      width: 150,
      render: (_, branch) => {
        if (!storeCodesLoaded) return <span style={{ color: tokens.color.ink3 }}>Đang tải</span>;
        const count = activeStoreCodeCountByBranch.get(branch.id) ?? 0;
        return count > 0 ? (
          <StatusTag color="success" icon={<Storefront size={12} aria-hidden />}>
            {count} mã
          </StatusTag>
        ) : (
          <StatusTag color="error">Chưa khai</StatusTag>
        );
      },
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 210,
      align: 'right',
      render: (_, branch) => {
        const shiftCount = activeShiftCountByBranch.get(branch.id) ?? 0;
        const storeCodeCount = activeStoreCodeCountByBranch.get(branch.id) ?? 0;

        // Menu khai báo dùng chung — người dùng chọn khai mục nào TÙY THỨ TỰ (RL-214: bỏ ép tuần
        // tự Ca làm → STK → POS). STK ngân hàng nằm trong menu như một tùy chọn ngang hàng.
        const setupItems = [
          { key: 'shifts', label: 'Khai ca làm' },
          { key: 'bank', label: 'Quản lý STK ngân hàng' },
          { key: 'pos', label: 'Khai mã POS' },
        ];
        const onSetup = ({ key }: { key: string }) => {
          if (key === 'shifts') setShiftsBranch(branch);
          else if (key === 'bank') setBankAccountsBranch(branch);
          else if (key === 'pos') setStoreCodesBranch(branch);
        };

        // Chỉ quyết định khi đã tải xong số ca lẫn danh sách mã POS — sớm hơn thì nháy nút rồi đổi.
        const ready = activeShiftCountByBranch.has(branch.id) && storeCodesLoaded;

        // Chưa đủ 2 tiêu chí vận hành BẮT BUỘC (ca làm + mã POS; STK ngân hàng không tính — RL-214):
        // nút CHÍNH nổi bật, nhưng là DROPDOWN để chọn khai bất kỳ mục nào. Nhãn giữ theo quy tắc cũ
        // (ưu tiên ca làm, rồi mã POS) để người quản lý biết việc gấp nhất.
        if (ready && (shiftCount === 0 || storeCodeCount === 0)) {
          const label = shiftCount === 0 ? 'Khai ca ngay' : 'Khai mã POS';
          return (
            <Dropdown trigger={['click']} menu={{ items: setupItems, onClick: onSetup }}>
              <Button
                size="small"
                type="primary"
                icon={<CaretDown size={12} aria-hidden />}
                iconPosition="end"
              >
                {label}
              </Button>
            </Dropdown>
          );
        }

        // Đã đủ ca làm + mã POS (hoặc đang tải): MỘT nút "Sửa" mở dropdown — giữ nguyên 100% luồng
        // chỉnh sửa cũ. Ca làm/Tài khoản/Mã POS quản lý ở drawer riêng nên gộp chung một nút.
        return (
          <Dropdown
            menu={{
              items: [
                { key: 'edit', label: 'Sửa thông tin' },
                { type: 'divider' },
                { key: 'shifts', label: 'Ca làm' },
                { key: 'bank', label: 'Tài khoản ngân hàng' },
                { key: 'pos', label: 'Mã POS' },
              ],
              onClick: ({ key }) => {
                if (key === 'edit') setEditingBranch(branch);
                else if (key === 'shifts') setShiftsBranch(branch);
                else if (key === 'bank') setBankAccountsBranch(branch);
                else if (key === 'pos') setStoreCodesBranch(branch);
              },
            }}
          >
            <Button size="small" icon={<CaretDown size={12} aria-hidden />} iconPosition="end">
              Sửa
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  if (chainsQuery.isPending || branchesQuery.isPending) {
    return (
      <>
        <PageHeader title="Chuỗi và chi nhánh" subtitle="Dữ liệu nền" />
        <PageBody>
          <LoadingState rows={6} />
        </PageBody>
      </>
    );
  }

  if (chainsQuery.isError || branchesQuery.isError) {
    return (
      <>
        <PageHeader title="Chuỗi và chi nhánh" subtitle="Dữ liệu nền" />
        <PageBody>
          <ErrorState
            error={chainsQuery.error ?? branchesQuery.error}
            onRetry={() => {
              void chainsQuery.refetch();
              void branchesQuery.refetch();
            }}
          />
        </PageBody>
      </>
    );
  }

  const chains = chainsQuery.data;

  return (
    <>
      <PageHeader
        title="Chuỗi và chi nhánh"
        subtitle="Dữ liệu nền"
        actions={
          canManageChains ? (
            <>
              <Button onClick={() => setIsChainModalOpen(true)}>Thêm chuỗi</Button>
              {/* Nút toàn cục giống prototype (a-branches) — trước đây chỉ có "Thêm chi nhánh"
                  trong từng thẻ chuỗi. `BranchFormModal` bắt buộc biết chuỗi nào trước khi mở
                  nên ở đây hỏi qua dropdown thay vì đoán chuỗi mặc định, tái dùng đúng modal
                  và state `creatingBranchForChain` sẵn có. */}
              <Dropdown
                disabled={chainsQuery.data?.length === 0}
                menu={{
                  items: (chainsQuery.data ?? []).map((c) => ({ key: c.id, label: c.name })),
                  onClick: ({ key }) => {
                    const target = chainsQuery.data?.find((c) => c.id === key);
                    if (target) setCreatingBranchForChain(target);
                  },
                }}
              >
                <Button type="primary" icon={<Plus size={14} aria-hidden />}>
                  Thêm chi nhánh
                </Button>
              </Dropdown>
            </>
          ) : null
        }
      />

      <PageBody>
        <div style={{ marginBottom: tokens.space.sectionGap }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>
            {chains.length} chuỗi · {activeBranchCount} chi nhánh đang hoạt động
          </h2>
          <Typography.Paragraph style={{ marginTop: 6, marginBottom: 0, color: tokens.color.ink2 }}>
            Mỗi chi nhánh cần khai đủ tọa độ, số bàn theo loại, <strong>ca làm</strong> và mã cửa
            hàng POS. Bạn có thể khai các mục theo bất kỳ thứ tự nào. Tài khoản ngân hàng nhận
            tiền là tùy chọn, chỉ cần khi quét hóa đơn VietQR. Thiếu ca làm thì nhân viên không
            mở được ca; thiếu mã POS thì không tích điểm được từ hóa đơn định dạng POS.
          </Typography.Paragraph>
        </div>

        {chains.length === 0 && (
          <Alert
            type="info"
            showIcon
            icon={<Info size={18} aria-hidden />}
            message="Chưa có chuỗi nào"
            description="Tạo chuỗi đầu tiên rồi thêm chi nhánh cho nó."
          />
        )}

        {chains.map((chain) => (
          <ChainBranchesCard
            key={chain.id}
            chain={chain}
            branches={branchesByChain.get(chain.id) ?? []}
            columns={columns}
            activeShiftCountByBranch={activeShiftCountByBranch}
            activeStoreCodeCountByBranch={activeStoreCodeCountByBranch}
            storeCodesLoaded={storeCodesLoaded}
            canManageChains={canManageChains}
            onEditChain={setEditingChain}
            onReload={() => void branchesQuery.refetch()}
          />
        ))}
      </PageBody>

      <ChainFormModal
        chain={editingChain}
        creating={isChainModalOpen}
        onClose={() => {
          setIsChainModalOpen(false);
          setEditingChain(null);
        }}
      />

      <BranchFormModal
        branch={editingBranch}
        chain={creatingBranchForChain}
        onClose={() => {
          setEditingBranch(null);
          setCreatingBranchForChain(null);
        }}
      />

      <BranchShiftsDrawer branch={shiftsBranch} onClose={() => setShiftsBranch(null)} />

      <BankAccountsDrawer branch={bankAccountsBranch} onClose={() => setBankAccountsBranch(null)} />
      <PosStoreCodesDrawer branch={storeCodesBranch} onClose={() => setStoreCodesBranch(null)} />
    </>
  );
}
